/* eslint-disable consistent-return */
/* eslint-disable array-callback-return */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import InformationFillIcon from "remixicon-react/InformationFillIcon";
import { Button, Tooltip } from "@mui/material";
import debounce from "lodash/debounce";
import { EdbStatus, PreparationOrderStatus } from "$types";
import AvailableQtyBadge from "$components/EDB/AvailableQtyBadge";
import { EDBService, EntrepotService } from "$services";
import PopperButtons from "$containers/EDB/PopperButtons";
import QuantityInput from "$components/EDB/QuantityInput";
import useAuth from "$hooks/useAuth";
import EntrepotCell from "$components/EDB/EntrepotCell";
import ProductReference from "$components/EDB/ProductReference";
import { IProduct } from "$containers";
import { useStyles as useEDBProfileClasses } from "$containers/EDB/styles/EdbProfile.Styles";
import { queryClient } from "$utils/queryClient";
import logger from "$utils/logger";
import { CacheInvalidationAction } from "$utils/cache";
import { GetAggregatedOpData } from "$services/EDB.service";
import { theme } from "$utils/theme/bataillog";

const useEDBProfile = (idEDb: string) => {
  const edbClasses = useEDBProfileClasses();
  const [refreshEdb, setRefreshEdb] = useState<any>([]);
  const [, setRefreshStatusEdb] = useState(false);
  const [statusEDB, setStatusEDB] = useState<EdbStatus>(EdbStatus.CART_CREATED);
  const [refreshOp, setRefreshOp] = useState(false);
  const [produit, setProduit] = useState<any>(null);
  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [productEdbAddToOP, setProductEdbAddToOP] = useState<any>(null);
  const [productToDeleteFromEdb, setProductToDeleteFromEdb] =
    useState<any>(null);
  const [edb, setEdb] = useState<any>(null);
  const { token, isAdmin } = useAuth();
  const [tInfoHeader, setTInfoHeader] = useState<any[]>([]);
  const [tInfoData, setTInfoData] = useState<any[]>([]);
  const [tbody, setTbody] = useState<any[]>([]);
  const [selectedSubstituteProduct, setSelectedSubstituteProduct] =
    useState<any>(null);
  const [openModalSubstituteProduct, setOpenModalSubstituteProduct] =
    useState<boolean>(false);
  //  const [tbody, setTbody] = usePersistedState([], 'tbody', null);

  const [initialTbody, setInitialTbody] = useState<any[]>([]);
  const [opList, setOpList] = useState<any>([]);
  const [isOpListLoaded, setIsOpListLoaded] = useState(false);
  const [disableButtonDelete, setDisableButtonDelete] = useState(true);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [isModifyingQuantityEnabled, setIsModifyingQuantityEnabled] =
    useState(false);
  const [isCreatingOp, setIsCreatingOp] = useState(false);
  // const TempEntrepotValue = useRef<any>({});
  const [quantityEntrepotMap, setQuantityEntrepotMap] = useState<
    Map<string, number>
  >(new Map());
  const [quantityMap, setQuantityMap] = useState<Map<string, number>>(
    new Map()
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [productPopin, setProductPopin] = useState<IProduct | undefined>(
    undefined
  );
  const [warehouses, setWarehouses] = useState([]);

  const isDisabled =
    edb?.isDisable ||
    edb?.projectDisable ||
    edb?.companyDisable ||
    edb?.dockyardDisable ||
    edb?.status === EdbStatus.REFUSED ||
    !isAdmin();

  const tInfoHeaderDefault = useMemo(
    () => [
      {
        id: "référence",
        label: (
          <>
            référence
            <Tooltip title="Merci de survoler la référence pour afficher la désignation">
              <Button style={{ minWidth: 35, color: "#7A869A" }}>
                <InformationFillIcon />
              </Button>
            </Tooltip>
          </>
        ),
        minWidth: 75,
      },
      {
        id: "unite",
        label: "unité",
        minWidth: 50,
      },
      {
        id: "qtyAvalaible",
        label: (
          <span>
            Qte en stock
            <br />
            hors réservation
          </span>
        ),
        minWidth: 50,
      },
    ],
    []
  );

  // Keep the original fetchEdb as a fallback method
  const fetchEdbOriginal = useCallback(async () => {
    try {
      if (idEDb) {
        let edbData;
        edbData = await queryClient.getQueryData([
          CacheInvalidationAction.GET_EDB_BY_ID,
          idEDb,
        ]);
        if (!edbData) {
          edbData = await queryClient.fetchQuery(
            [CacheInvalidationAction.GET_EDB_BY_ID, idEDb],
            () => EDBService.getEdbById(idEDb),
            { staleTime: 5 * 60 * 1000, retryDelay: 500 }
          );
          await queryClient.setQueryData(
            [CacheInvalidationAction.GET_EDB_BY_ID, idEDb],
            edbData
          );
        }

        setEdb(edbData);
        let listOp;
        listOp = await queryClient.getQueryData([
          CacheInvalidationAction.GET_OPS_BY_EDB,
          idEDb,
        ]);
        if (!listOp) {
          listOp = await queryClient.fetchQuery(
            [CacheInvalidationAction.GET_OPS_BY_EDB, idEDb],
            () => EDBService.GetOpsByEdb(token, idEDb),
            { staleTime: 5 * 60 * 1000, retryDelay: 500 }
          );
          await queryClient.setQueryData(
            [CacheInvalidationAction.GET_OPS_BY_EDB, idEDb],
            listOp
          );
        }
        setOpList(listOp);
        setIsOpListLoaded(true);
      }
    } catch (err: any) {
      logger.error(err);
      console.error("error fetching edb");
    }
  }, [idEDb, token]);

  // Replace the existing fetchEdb function with this one
  const fetchEdb = useCallback(async () => {
    try {
      if (idEDb) {
        setIsLoading(true);

        // Fetch aggregated OP data through the gateway
        const aggregatedData = await GetAggregatedOpData(token, idEDb);

        // Set state with the aggregated data
        setEdb(aggregatedData.edb);
        setOpList(aggregatedData.ops);
        setIsOpListLoaded(true);

        // Cache the aggregated data for future reference
        await queryClient.setQueryData(
          [CacheInvalidationAction.GET_EDB_BY_ID, idEDb],
          aggregatedData.edb
        );

        await queryClient.setQueryData(
          [CacheInvalidationAction.GET_OPS_BY_EDB, idEDb],
          aggregatedData.ops
        );

        setIsLoading(false);
      }
    } catch (err: any) {
      logger.error(err);
      setIsLoading(false);
      console.error("Error fetching aggregated OP data:", err);
      // Try again with the original approach as fallback
      fetchEdbOriginal();
    }
  }, [fetchEdbOriginal, idEDb, token]);

  useEffect(() => {
    fetchEdb();
  }, [fetchEdb, idEDb, refreshEdb]);

  // get warehouses
  const fecthWarehouses = useCallback(async () => {
    if (edb) {
      const filters = {
        ...{
          $withContributorOf: edb?.company?._id,
        },
      };
      const queryKey = [CacheInvalidationAction.GET_ENTREPOTS, filters];
      let getWarehouses: any = await queryClient.getQueryData(queryKey);
      if (!getWarehouses) {
        getWarehouses = await queryClient.fetchQuery(
          queryKey,
          () => EntrepotService.getEntrepots(filters),
          { cacheTime: 5 * 60 * 1000 }
        );
        queryClient.setQueryData(queryKey, getWarehouses);
      }
      setWarehouses(getWarehouses?.data || []);
    }
  }, [edb]);

  // refresh entrepot list
  useEffect(() => {
    const doGetEntrepots = async () => {
      const edbCompanyId = edb?.company?._id;
      if (edbCompanyId) {
        fecthWarehouses();
      }
    };
    doGetEntrepots();
  }, [edb, token, fecthWarehouses, warehouses]);

  // update tbody => qtyAsked by product after fetching OPs of edb
  const getProductQtyFromOP = useCallback(() => {
    const productOP = initialTbody.map((tdata) => {
      const sumQtyFromOp = opList
        .filter((op: any) => op.status !== PreparationOrderStatus.CANCELLED)
        .map((op: any) => {
          const qty = op.products
            .filter(
              (product: any) =>
                (product.relatedProduct._id === tdata.relatedProduct._id ||
                  product.relatedProduct._id === tdata?.productReplaced?._id) &&
                product.receptionReferenceId === tdata.receptionReferenceId
            )
            .reduce((total: number, product: any) => total + product.qty, 0);
          return {
            qty,
          };
        })
        .reduce((total: number, product: any) => total + product.qty, 0);
      return {
        qtyAsked: sumQtyFromOp,
        relatedProduct: tdata.relatedProduct,
        receptionReferenceId: tdata.receptionReferenceId,
      };
    });
    return productOP;
  }, [initialTbody, opList]);

  useEffect(() => {
    const newTBody: any[] = [];
    edb?.products?.forEach((p: any) => {
      const quantityMapData = quantityMap.get(p._id);
      const data = {
        _id: p._id,
        warehouse: p.warehouse,
        relatedProduct: p.relatedProduct,
        receptionReferenceId: p.receptionReferenceId,
        qty: quantityMapData || p.qty,
        qtyAsked: quantityMapData || p.qty,
        qtyAvalaible: p.stock.quantity,
        productReplaced: p.productReplaced || null,
      };
      warehouses.forEach((e: any) => {
        if (p.receptionReferenceId) {
          const entrepotOps = opList.filter(
            (op) =>
              op.entrepot._id === e._id &&
              op.status !== PreparationOrderStatus.CANCELLED
          );
          const qtyInOps = entrepotOps.reduce((totalQty, op) => {
            const foundProduct = op.products.find(
              (opProduct) =>
                opProduct.relatedProduct?._id === p.relatedProduct?._id &&
                opProduct.receptionReferenceId === p.receptionReferenceId
            );
            return totalQty + (foundProduct ? foundProduct.qty : 0);
          }, 0);
          const availableQty =
            e._id === p.warehouse ? (quantityMapData || p.qty) - qtyInOps : 0;

          data[e.name] = {
            qtyAsked: 0,
            qtyAvalaible: availableQty,
            qtyAvalaibleInitial: availableQty,
          };
          // override qty available: for a referenced product, it's just the quantity in one entrepot
          data.qtyAvalaible =
            availableQty !== 0 ? availableQty : data.qtyAvalaible;
        } else {
          const qtyStock = p.stock.stocks
            .filter((s: any) => s?.entrepot?._id === e?._id)
            .reduce((total: number, s: any) => total + s.quantity, 0);

          data[e.name] = {
            qtyAsked: 0,
            qtyAvalaible: qtyStock,
            qtyAvalaibleInitial: qtyStock,
          };
        }
      });
      newTBody.push(data);
    });
    // check if duplicates products exists and merge (if not from a reception)
    const productsBody: any[] = [];
    newTBody.forEach((body: any) => {
      const isAReferencedProduct = !!body.receptionReferenceId;
      const isNew = !productsBody.find(
        (pr) =>
          pr?.relatedProduct?._id === body?.relatedProduct?._id &&
          pr?.receptionReferenceId === body?.receptionReferenceId
      );
      if (isNew && !isAReferencedProduct) {
        const items = newTBody.filter(
          (pr) =>
            (pr?.relatedProduct?._id === body?.relatedProduct?._id ||
              pr?.relatedProduct?._id === body?.productReplaced?._id) &&
            !pr.receptionReferenceId
        );
        const value = items.reduce((cumul, last) => ({
          ...last,
          qtyAsked: cumul.qtyAsked + last.qtyAsked,
        }));
        productsBody.push(value);
      }
      if (isNew && isAReferencedProduct) {
        productsBody.push(body);
      }
    });
    setTbody(productsBody);
    setInitialTbody(productsBody);
    setRefreshOp((prevState: boolean) => !prevState);
  }, [edb, warehouses, quantityMap, opList]);

  const isEdbProductDeletable = useCallback(
    (idProduct: string) => {
      let rep = true;
      for (let i = 0; i < opList.length; i += 1) {
        if (opList[i].status !== PreparationOrderStatus.CANCELLED) {
          const index = opList[i].products.findIndex(
            (p: any) => p.relatedProduct._id === idProduct
          );
          if (index > -1 && opList[i].status > 0) {
            rep = false;
            break;
          }
        }
      }
      return rep;
    },
    [opList]
  );

  const disableRemoveOp = useCallback(() => {
    let rep = true;
    if (opList.length === 0 && !isFirstRender) rep = false;
    opList.map((op: any) => {
      if (op.status > PreparationOrderStatus.CREATED) rep = true;
    });
    return rep;
  }, [isFirstRender, opList]);

  // update tbody after fetching/update opList
  useEffect(() => {
    (async () => {
      const productsFromOP = getProductQtyFromOP();
      setIsFirstRender(false);
      const tbodyFiltered = initialTbody
        .filter((tdata: any, index: number) => {
          if (
            productsFromOP[index] &&
            tdata.qtyAsked <= productsFromOP[index].qtyAsked &&
            (tdata.relatedProduct._id ===
              productsFromOP[index].relatedProduct._id ||
              tdata?.productReplaced?._id ===
                productsFromOP[index].relatedProduct._id) &&
            tdata.receptionReferenceId ===
              productsFromOP[index].receptionReferenceId
          ) {
            return false;
          }
          return true;
        })
        .map((tdata: any) => {
          const row = {
            ...tdata,
            qtyAsked: tdata.qtyAsked,
          };
          productsFromOP.map((opProduct: any) => {
            if (
              tdata.qtyAsked > opProduct.qtyAsked &&
              (tdata?.relatedProduct?._id === opProduct.relatedProduct?._id ||
                tdata?.productReplaced?._id ===
                  opProduct.relatedProduct?._id) &&
              tdata?.receptionReferenceId === opProduct.receptionReferenceId
            ) {
              row.qtyAsked = tdata.qtyAsked - opProduct.qtyAsked;
            }
          });
          warehouses.map((e: any) => {
            const quantityMapData = quantityEntrepotMap.get(
              `${tdata._id}-${e.name}`
            );
            if (row[e?.name]) {
              row[e?.name].qtyAsked = quantityMapData || 0;
            }
          });
          return row;
        });

      setTbody(tbodyFiltered);
      setDisableButtonDelete(disableRemoveOp());
    })();
  }, [
    disableRemoveOp,
    warehouses,
    getProductQtyFromOP,
    initialTbody,
    opList,
    quantityEntrepotMap,
    refreshOp,
  ]);

  const handleChangeQtyAsked = useCallback(
    async (event: any, rowProduct: any, index: number) => {
      const newtbody = [...tbody];
      const newQty =
        event.target.value && typeof event.target.value === "string"
          ? parseInt(event.target.value, 10) === 0
            ? 1
            : parseInt(event.target.value, 10)
          : event.target.value === 0
          ? 1
          : event.target.value;
      newtbody[index].qtyAsked = newQty;
      setQuantityMap(quantityMap.set(rowProduct._id, newQty));

      setTbody(newtbody);
    },
    [quantityMap, tbody]
  );

  const handleChangeQtyAskedEntrepot = useCallback(
    (value: any, keyEntrepot: string, index: number) => {
      setTbody((currentTbody: any[]) => {
        const newtbody = [...currentTbody];
        const { receptionReferenceId } = newtbody[index];
        const { qtyAvalaibleInitial } = newtbody[index][keyEntrepot];
        const newQty = parseInt(
          receptionReferenceId
            ? Math.min(qtyAvalaibleInitial, value || 0) // For a referenced product, you can't pick more than what is available
            : value || 0,
          10
        );
        newtbody[index][keyEntrepot].qtyAsked = newQty;
        setQuantityEntrepotMap(
          new Map(quantityEntrepotMap).set(
            `${newtbody[index]._id}-${keyEntrepot}`,
            newQty
          )
        );
        // si qtyAvalaible - newQty < 0, ne pa mettre en negatif le QtyAvalaible
        const newQtyAvalaible = qtyAvalaibleInitial - newQty;
        newtbody[index][keyEntrepot].qtyAvalaible = newQtyAvalaible;
        return newtbody;
      });
    },
    [quantityEntrepotMap]
  );

  const handleChangeAllQtyAskedEntrepot = useCallback(
    (keyEntrepot: string) => {
      let newMap = new Map(quantityEntrepotMap);
      const newTBody = tbody.map((row: any, index: number) => {
        const newRow = { ...row };
        const { qtyAsked } = row;
        const isAReferencedProduct = !!row.receptionReferenceId;
        if (
          qtyAsked !== row[keyEntrepot].qtyAsked &&
          (!isAReferencedProduct || row[keyEntrepot].qtyAvalaible !== 0) // referenced product can't ask if nothing is available
        ) {
          newRow[keyEntrepot].qtyAsked = qtyAsked;
          newRow[keyEntrepot].qtyAvalaible =
            row[keyEntrepot].qtyAvalaible - qtyAsked;
          newMap = new Map(newMap).set(
            `${tbody[index]._id}-${keyEntrepot}`,
            qtyAsked
          );
        }
        return newRow;
      });

      setQuantityEntrepotMap(newMap);

      setTbody(newTBody);
    },
    [quantityEntrepotMap, tbody]
  );

  // set Tbody after fetching edb
  useEffect(() => {
    (async () => {
      // setEdb status
      setStatusEDB(edb?.status || "");

      // format data for Table component: thead and tbody
      const headerEntrepots: any[] = [];
      const newThead = [
        ...tInfoHeaderDefault,
        {
          id: "qtyAsked",
          label: (
            <div style={{ display: "flex", alignItems: "center" }}>
              <span>
                quantité
                <br />
                demandée
              </span>
              {/* Afin déviter les bugs de calcul et de modification de quantité des produits (embarqués ou non) depuis l'étape 3 de l'EDB,
              on désactive la modification de la quantité pour l'instant
              isModifyingQuantityEnabled ? (
                <>
                  <UIButton
                    style={{ marginLeft: 15, minWidth: 35, color: "red" }}
                    onClick={() => {
                      setIsModifyingQuantityEnabled(false);
                    }}
                  >
                    <DeleteBinFillIcon />
                  </UIButton>
                  <UIButton
                    className={edbClasses.blueIcon}
                    style={{ minWidth: 35 }}
                    onClick={async () => {
                      const opProd = getProductQtyFromOP();
                      const newProducts = tbody.map(
                        (body: any, index: number) => {
                          if (body.qtyAsked === 0) {
                            tbody[index].qtyAsked = 1;
                          }
                          return {
                            _id: edb.products[index]._id,
                            receptionReferenceId:
                              edb.products[index].receptionReferenceId,
                            qty:
                              (opProd.find(
                                ({ relatedProduct }) =>
                                  relatedProduct._id ===
                                    edb.products[index].relatedProduct?._id &&
                                  relatedProduct.receptionReferenceId === null
                              )?.qtyAsked ?? 0) +
                              (body.qtyAsked === 0 ? 1 : body.qtyAsked),
                          };
                        }
                      );
                      await EDBService.UpdateEDBProducts(
                        token,
                        { newProducts },
                        edb._id
                      );
                      setIsModifyingQuantityEnabled(false);
                    }}
                  >
                    <SaveFillIcon />
                  </UIButton>
                </>
              ) : (
                <UIButton
                  className={edbClasses.blueIcon}
                  style={{
                    marginLeft: 15,
                    minWidth: 35,
                  }}
                  onClick={() => setIsModifyingQuantityEnabled(true)}
                >
                  <Edit2LineIcon />
                </UIButton>
              ) */}
            </div>
          ),
          width: 100,
        },
        ...warehouses
          .map((e: any, index: number) => {
            // if (e?.activities?.includes("central")) {
            headerEntrepots.push(`qt ${e.name}`);
            return {
              id: `entrepot${index}`,
              label: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: 160,
                    paddingRight: 20,
                  }}
                >
                  <div style={{ flex: 1, wordBreak: "break-word" }}>
                    {e.name}
                  </div>
                  <Button
                    variant="outlined"
                    disabled={isDisabled}
                    onClick={() => {
                      handleChangeAllQtyAskedEntrepot(e.name);
                    }}
                    style={{
                      marginLeft: 8,
                      // marginRight: 30,
                      minWidth: 24,
                      height: 24,
                      padding: 0,
                      fontSize: 20,
                    }}
                    sx={{
                      backgroundColor: "transparent !important",
                      border: "1px solid rgba(0, 0, 0, 0.23) !important",
                      color: "black !important",
                      "&:hover": {
                        backgroundColor: `${theme.color.gray2} !important`, // light gray hover
                      },
                      "&:focus": {
                        backgroundColor: `${theme.color.gray1} !important`,
                      },
                      "&.Mui-disabled": {
                        borderColor: "rgba(0, 0, 0, 0.12) !important",
                        color: "rgba(0, 0, 0, 0.26) !important",
                      },
                    }}
                  >
                    =
                  </Button>
                </div>
              ),
            };
            // }
          })
          .filter((warehouse: any) => warehouse !== undefined),
        {
          id: "action",
          label: "Action",
          minWidth: 75,
          style: {
            width: 56,
          },
        },
      ];
      setTInfoHeader(newThead);
    })();
  }, [
    edb,
    isModifyingQuantityEnabled,
    tbody,
    warehouses,
    getProductQtyFromOP,
    tInfoHeaderDefault,
    token,
    handleChangeAllQtyAskedEntrepot,
    edbClasses.blueIcon,
    isDisabled,
  ]);

  // OP creation, get list o entrepot which has products
  const handleCreateOP = async () => {
    setIsCreatingOp(true);
    const opByEntrepot = warehouses
      .map((e) => {
        const row = {
          code: "",
          reference: "",
          refPreparationEntrepot: "",
          edb: edb?._id,
          chantier: edb?.dockyard?._id,
          company: edb?.company?._id,
          entrepot: e._id,
          shipType: edb?.dispositionType,
          shipEntrepotName: edb?.entrepotName,
          shipAdress: edb?.dispositionAdress,
          shipPostalCode: edb?.dispositionPostalCode,
          shipCity: edb?.dispositionCity,
          shipCountry: edb?.dispositionCountry,
          shipPlace: edb?.dispositionPlace,
          products: tbody
            .map((t: any) => {
              const product = {
                productID: t.relatedProduct._id,
                qty: t[e.name].qtyAsked,
                receptionReferenceId: t.receptionReferenceId,
              };
              return product;
            })
            .filter((p: any) => p.qty > 0),
        };
        return row;
      })
      .filter((op) => op.products.length > 0);
    await Promise.all(
      opByEntrepot.map(async (op: any) => {
        try {
          await EDBService.AddOP(token, op);
        } catch (err: any) {
          return { error: (err as any).message };
        }
      })
    );
    // setOpList(opByEntrepot);

    // const listOP = await EDBService.GetOpsByEdb(token, idEDb);
    // @todo: check if we need to invalidate cache, not done in backend ?
    queryClient.removeQueries([CacheInvalidationAction.GET_EDB_BY_ID, idEDb]);
    queryClient.removeQueries([CacheInvalidationAction.GET_OPS_BY_EDB, idEDb]);

    fetchEdb();
    // getOPs(true);
    // TempEntrepotValue.current = {};

    setQuantityEntrepotMap(new Map());

    // setOpList(listOP);
    setIsCreatingOp(false);
  };

  const handleChangeOP = async (
    indexOP: number,
    op: any,
    sendData = false,
    _refreshStatus = false
  ) => {
    // change data in state
    const newOpList = [...opList];
    newOpList[indexOP] = op;

    // change data in DB
    if (sendData) {
      try {
        const updatedOp = await EDBService.EditOP(
          token,
          {
            ...op,
            company: op.company._id,
            entrepot: op.entrepot._id,
            edb: op.edb._id,
            chantier: op.chantier._id,
          },
          op._id
        );

        newOpList[indexOP].statusHistory = updatedOp.statusHistory;

        if (_refreshStatus || op.status !== opList[indexOP].status) {
          await fetchEdb();
          return;
        }
      } catch (err: any) {
        return { error: (err as any).message };
      }
    }
    setOpList(newOpList);

    if (_refreshStatus) {
      setRefreshStatusEdb((prevState: boolean) => !prevState);
      await fetchEdb();
    }
  };

  const deboucedSave = useRef(
    debounce((NextValue: any) => NextValue.action(), 50)
  ).current;

  const updateProductOPQty = async (
    indexOP: number,
    indexProduct: number,
    productQty: number
  ) => {
    const newOpList = [...opList];
    // to do, update OPItem in DB
    const action = async () => {
      try {
        if (productQty === 0) {
          const idOpItem = newOpList[indexOP].products[indexProduct]._id;
          newOpList[indexOP].products = newOpList[indexOP].products.filter(
            (product: any) =>
              product._id !== newOpList[indexOP].products[indexProduct]._id
          );
          await EDBService.DeleteOPItem(token, idOpItem);
          if (newOpList[indexOP].products.length === 0) {
            await onCancelOP(newOpList[indexOP]._id);
          } else {
            fetchEdb();
          }
          setOpList(newOpList);
        } else {
          newOpList[indexOP].products[indexProduct].qty = productQty;
          await EDBService.EditOPItem(
            token,
            {
              ...newOpList[indexOP].products[indexProduct],
              relatedProduct:
                newOpList[indexOP].products[indexProduct].relatedProduct._id,
            },
            newOpList[indexOP].products[indexProduct]._id
          );
          // Don't call fetchEdb() here - the local state update is sufficient
          // and calling fetchEdb() causes unnecessary page-like refreshes
          setOpList(newOpList);
        }
      } catch (err: any) {
        setOpList(newOpList);
        return { error: (err as any).message };
      }
    };
    deboucedSave({ newOpList, action });
  };

  // popin cancel OP ==> delete OP
  const onCancelOP = async (idOp: string) => {
    try {
      const op = opList.find((o: any) => o._id === idOp);

      if (op && op.status === PreparationOrderStatus.CREATED) {
        await EDBService.DeleteOP(token, idOp);
      } else {
        await EDBService.EditOPStatus(
          token,
          { status: PreparationOrderStatus.CANCELLED },
          idOp
        );
      }
      fetchEdb();
    } catch (err: any) {
      return { error: (err as any).message };
    }
  };

  // cancelOpitems
  const onDeleteOPItem = async (idOpItem: string, indexOP: number) => {
    try {
      const newOpList = [...opList];
      newOpList[indexOP].products = newOpList[indexOP].products.filter(
        (product: any) => product._id !== idOpItem
      );
      if (newOpList[indexOP]?.products?.length === 0) {
        await onCancelOP(newOpList[indexOP]._id);
      } else {
        await EDBService.DeleteOPItem(token, idOpItem);
        setOpList(newOpList);
        fetchEdb();
      }
    } catch (err: any) {
      return { error: (err as any).message };
    }
  };

  const onUntrackIntegratedProductFromEdb = useCallback(
    async (edbId: string, edbItemId: string) => {
      try {
        await EDBService.DeleteEDBItem(token, edbId, edbItemId);
        const refresh = [...refreshEdb];
        refresh.push(new Date());
        setRefreshEdb(refresh);
      } catch (err: any) {
        return { error: (err as any).message };
      }
    },
    [refreshEdb, token]
  );

  const onDeleteItemFromEdb = async (idProduct: string, idEdb: string) => {
    try {
      // delete product from edb
      await EDBService.DeleteEDBItemProduct(token, idEdb, idProduct);
      // getEdbs(true);

      const refresh = [...refreshEdb];
      refresh.push(new Date());
      setRefreshEdb(refresh);
    } catch (err: any) {
      return { error: (err as any).message };
    }
  };

  const onDeleteEdb = async (idEdb: string) => {
    try {
      await EDBService.DeleteEDB(token, idEdb);
      // getEdbs(true);
    } catch (err: any) {
      return { error: (err as any).message };
    }
  };

  const onCancelEdb = async (idEdb: string) => {
    try {
      await EDBService.cancelEDB(token, idEdb);
      await fetchEdb();
    } catch (err: any) {
      return { error: (err as any).message };
    }
  };

  const onSubstituteProduct = () => {
    // console.log('to do by rivo, substitute popin');
  };

  const substituteProduct = async (
    idEdb: string,
    idProduct: string,
    substituteBy: string
  ) => {
    try {
      const res = await EDBService.SubstituteProductEdb(
        token,
        idEdb,
        idProduct,
        substituteBy
      );

      const edbData = await EDBService.getEdbById(idEDb);
      setEdb(edbData);
      const listOP = await EDBService.GetOpsByEdb(token, idEDb);
      setOpList(listOP);
      return res;
      // getEdbs(true);
    } catch (err: any) {
      return { error: (err as any).message };
    }
  };

  const handleOnProductClick = useCallback((product: any, stock: any) => {
    if (product.relatedProduct?.isReferenced) {
      setProductPopin({ ...product.relatedProduct, stock });
    } else {
      setProduit(product);
      setFormOpen(true);
    }
  }, []);

  const substitutionProductEDB = async (
    idEdb: string,
    productId: string,
    substitutionQty: number,
    substitutions: any
  ) => {
    try {
      setIsLoading(true);
      const res = await EDBService.SubstitutionProductEdb(
        token,
        idEdb,
        productId,
        substitutionQty,
        substitutions
      );

      const edbData = await EDBService.getEdbById(idEDb);
      setEdb(edbData);
      const listOP = await EDBService.GetOpsByEdb(token, idEDb);
      setOpList(listOP);
      setIsLoading(false);
      return res;
      // getEdbs(true);
    } catch (err: any) {
      return { error: (err as any).message };
    }
  };

  const onAddToOp = (product: any, index: number) => {
    setProductEdbAddToOP({
      index,
      product,
    });
  };

  const validateProductAddToOp = (
    value: any,
    keyEntrepot: string,
    index: number
  ) => {
    handleChangeQtyAskedEntrepot(value, keyEntrepot, index);
    handleCreateOP();
  };

  // rendered Tbody
  useEffect(() => {
    const newTInfobody = tbody.map((d: any, index: number) => {
      const row = [
        {
          value: (
            <ProductReference
              handleOnProductClick={handleOnProductClick}
              product={d}
              stock={edb?.products.find((p) => p._id === d._id)?.stock}
            />
          ),
        },
        {
          value: d?.relatedProduct?.unit,
        },
        {
          value: (
            <AvailableQtyBadge
              askedQty={d.qtyAsked}
              availableQty={d?.qtyAvalaible}
            />
          ),
        },
        {
          value: (
            <QuantityInput
              disabled={!isModifyingQuantityEnabled || isDisabled}
              handleChangeQtyAsked={handleChangeQtyAsked}
              index={index}
              product={d}
            />
          ),
        },
        ...warehouses.map((e: any) => {
          return {
            value: (
              <EntrepotCell
                entrepot={e}
                handleChangeQtyAskedEntrepot={handleChangeQtyAskedEntrepot}
                index={index}
                product={d}
                disabled={isDisabled}
              />
            ),
          };
        }),
        {
          value: (
            <PopperButtons
              isAnIntegratedProduct={!!d.receptionReferenceId}
              isDisabled={isDisabled}
              isProductDeletable={isEdbProductDeletable(d?.relatedProduct?._id)}
              onSubstitute={() => {
                setSelectedSubstituteProduct(d);
                setOpenModalSubstituteProduct(true);
                onSubstituteProduct();
              }}
              onAddToOp={() => onAddToOp(d, index)}
              onDeleteFromEdb={() =>
                setProductToDeleteFromEdb(d.relatedProduct)
              }
              onUntrackIntegratedProduct={() =>
                onUntrackIntegratedProductFromEdb(edb?._id, d._id)
              }
              opList={opList.filter(
                (op: any) =>
                  op.status === PreparationOrderStatus.CREATED &&
                  (!d.warehouse || op.entrepot._id === d?.warehouse)
              )}
            />
          ),
        },
      ];

      return row;
    });

    setTInfoData(newTInfobody);
  }, [
    tbody,
    isModifyingQuantityEnabled,
    warehouses,
    edb?.products,
    edb?._id,
    handleOnProductClick,
    handleChangeQtyAsked,
    isEdbProductDeletable,
    opList,
    handleChangeQtyAskedEntrepot,
    onUntrackIntegratedProductFromEdb,
    isDisabled,
  ]);

  const canCreateOp = useMemo(() => {
    const empty = quantityEntrepotMap.size === 0;
    let hasOneQtyPositive = false;
    // eslint-disable-next-line no-return-assign
    quantityEntrepotMap.forEach((v) =>
      v > 0 ? (hasOneQtyPositive = true) : null
    );
    return (
      !empty &&
      hasOneQtyPositive &&
      !isCreatingOp &&
      !isModifyingQuantityEnabled &&
      !isDisabled
    );
  }, [
    quantityEntrepotMap,
    isCreatingOp,
    isModifyingQuantityEnabled,
    isDisabled,
  ]);

  return {
    edb,
    canCreateOp,
    tInfoHeader,
    tInfoData,
    isCreatingOp,
    handleCreateOP,
    opList,
    isOpListLoaded,
    updateProductOPQty,
    handleChangeOP,
    onCancelOP,
    onDeleteOPItem,
    onDeleteEdb,
    onCancelEdb,
    disableButtonDelete,
    onDeleteItemFromEdb,
    productEdbAddToOP,
    setProductEdbAddToOP,
    validateProductAddToOp,
    productToDeleteFromEdb,
    setProductToDeleteFromEdb,
    substituteProduct,
    substitutionProductEDB,
    statusEDB,
    fetchEdb,
    isModifyingQuantityEnabled,
    setIsModifyingQuantityEnabled,
    isLoading,
    setIsLoading,
    selectedSubstituteProduct,
    setSelectedSubstituteProduct,
    openModalSubstituteProduct,
    setOpenModalSubstituteProduct,
    productPopin,
    setProductPopin,
    produit,
    setProduit,
    formOpen,
    setFormOpen,
    isDisabled,
  };
};

export default useEDBProfile;
